'use client';

import { useState } from 'react';

// Through the barrel, like its consumers, so a spec that mocks
// `@/infrastructure/auth` also drives this hook.
import { useAuth } from '@/infrastructure/auth';

const SWITCH_FAILED_MESSAGE = 'Could not switch role. Please try again.';

/**
 * A human message for a failed role switch or for the permissions read that
 * follows it. Only transport failures are translated (the fetch rejects with
 * a `TypeError` offline, a `TimeoutError` when aborted by a timeout); the
 * message of an HTTP error body reaches the user through `switchTo` untouched.
 */
export function roleSwitchErrorMessage(error: unknown): string {
  if (error instanceof DOMException && error.name === 'TimeoutError') {
    return 'Request timed out. Please check your connection and try again.';
  }
  if (error instanceof TypeError) {
    return 'Network error. Please check your connection and try again.';
  }
  return SWITCH_FAILED_MESSAGE;
}

/**
 * The platform roles the user holds and a switch between them
 * (POST /api/auth/switch-role, via `AuthProvider.switchRole`). Shared by the
 * profile menu's Role selector and the Access Denied page. A failed switch
 * leaves its reason in `error` (cleared by the next attempt).
 */
export function useRoleSwitch() {
  const { roleCode, availablePlatformRoles, switchRole } = useAuth();
  const [isSwitching, setIsSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const platformTenant = availablePlatformRoles[0];
  const roles = platformTenant?.roles ?? [];
  const currentRole = roles.find((r) => r.code === roleCode);

  /** Resolves `true` when the switch was accepted. */
  const switchTo = async (roleUuid: string): Promise<boolean> => {
    if (!platformTenant || isSwitching) return false;

    setIsSwitching(true);
    setError(null);
    try {
      const result = await switchRole(platformTenant.uuid, roleUuid);
      if (result?.success === false) {
        // The BFF's own message (the gateway's, or its fallback), not masked.
        setError(result.error || SWITCH_FAILED_MESSAGE);
        return false;
      }
      return true;
    } catch (caught) {
      console.error('Failed to switch role:', caught);
      setError(roleSwitchErrorMessage(caught));
      return false;
    } finally {
      setIsSwitching(false);
    }
  };

  return { roleCode, roles, currentRole, isSwitching, error, switchTo };
}
